import { createRouter, createWebHistory } from 'vue-router'
import store from '@/store';
import i18n from '@/i18n';
import { getAuthToken, checkCurrentAuthenticatedUser } from '@/services/auth';
//import { configAuth } from '@/services/auth'
import { isMobileDevice } from '@/services/deviceUtils'; // Import the utility function
import { forceServiceWorkerUpdate } from '@/registerServiceWorker';


// Lazy load components
const loadView = (view) => () => import(`@/views/${view}.vue`);
const loadComponent = (component) => () => import(`@/components/${component}.vue`);

// Define roles
const roleNames = {
  TenantAdmin: 'TenantAdmin',
  Facilitator: 'Facilitator',
  Participant: 'Participant',
  Observer: 'Observer',
};

// Define routes
const routes = [
  {
    path: '/',
    name: 'Start',
    component: loadView('Start'),
    meta: {titleKey: 'nav.pageTitleStart', title: 'Start', requiresAuth: false}},
  {
    path: '/dashboard',
    name: 'Dashboard',
    component: loadView('Dashboard'),
    meta: {titleKey: 'nav.pageTitleDashboard', title: 'Dashboard', requiresAuth: true},
    beforeEnter: async (to, from, next) => {
      // First check if it's a mobile device
      if (isMobileDevice()) {
        next({ name: 'MobileDashboard' });
        return;
      }

      // If not mobile, proceed with normal navigation
      next();
    }
  },
  {
    path: '/m-dashboard',
    name: 'MobileDashboard',
    component: loadView('MobileDashboard'),
    meta: {titleKey: 'nav.pageTitleMobileDashboard', title: 'Mobile Dashboard', requiresAuth: true}
  },
  {
    path: '/treasure-chest',
    name: 'TreasureChest',
    component: loadView('TreasureChest'),
    meta: {titleKey: 'nav.pageTitleTreasureChest', title: 'TreasureChest', requiresAuth: true},
    beforeEnter: blockObserverAppRole('MobileTreasureChest')
  },
  {
    path: '/m-treasure-chest',
    name: 'MobileTreasureChest',
    component: loadView('MobileTreasureChest'),
    meta: {titleKey: 'nav.pageTitleMobileTreasureChest', title: 'Mobile TreasureChest', requiresAuth: true},
    beforeEnter: blockObserverAppRole()
  },
  {
    path: '/notifications',
    name: 'Notifications',
    component: loadView('Notifications'),
    meta: {titleKey: 'nav.pageTitleNotifications', title: 'Notifications', requiresAuth: true},
    beforeEnter: redirectToMobile('MobileNotifications')
  },
  {
    path: '/m-notifications',
    name: 'MobileNotifications',
    component: loadView('MobileNotifications'),
    meta: {titleKey: 'nav.pageTitleMobileNotifications', title: 'Mobile Notifications', requiresAuth: true}
  },
  {
    path: '/journey-creation/:id?',
    name: 'JourneyCreation',
    component: loadView('JourneyCreation'),
    meta: {titleKey: 'nav.pageTitleJourneyCreation', title: 'JourneyCreation', requiresAuth: true, allowedRoles: [roleNames.TenantAdmin, roleNames.Facilitator]}
  },
  // {
  //   path: '/journey/map/:id?',
  //   name: 'JourneyMap',
  //   component: loadView('JourneyMap'),
  //   meta: {title: 'JourneyMap', requiresAuth: true}
  // },
  {
    path: '/journey/view/:id?',
    name: 'JourneyView',
    component: loadView('JourneyView'),
    meta: {titleKey: 'nav.pageTitleJourneyView', title: 'JourneyView', requiresAuth: true},
    beforeEnter: redirectObserverToProgressOverview
  },
  {
    path: '/journey/:journeyId/participant/:participantId',
    name: 'ParticipantJourneyOverview',
    component: loadView('ParticipantJourneyOverview'),
    meta: {title: 'ParticipantJourneyOverview', requiresAuth: true}
  },
  {
    path: '/journey/:journeyId/participants-progress',
    name: 'ParticipantsJourneyProgressOverview',
    component: loadView('ParticipantsJourneyProgressOverview'),
    meta: {title: 'ParticipantsJourneyProgressOverview', requiresAuth: true}
  },
  {
    path: '/journey/:journeyId/assignments-answers',
    name: 'JourneyAssignmentsAnswers',
    component: loadView('JourneyAssignmentsAnswers'),
    // any app role: the page checks the journey role (Author / Co-Author grade, Observer read-only for followed participants)
    // meta: {titleKey: 'nav.pageTitleJourneyAssignmentsAnswers', title: 'JourneyAssignmentsAnswers', requiresAuth: true, allowedRoles: [roleNames.TenantAdmin, roleNames.Facilitator]}
    meta: {titleKey: 'nav.pageTitleJourneyAssignmentsAnswers', title: 'JourneyAssignmentsAnswers', requiresAuth: true}
  },
  {
    path: '/m-journey/view/:id?',
    name: 'MobileJourneyView',
    component: loadView('MobileJourneyView'),
    meta: {titleKey: 'nav.pageTitleMobileJourneyView', title: 'Mobile Journey View', requiresAuth: true},
    beforeEnter: redirectObserverToProgressOverview
  },
  {
    path: '/m-journey/map/:id?',
    name: 'MobileJourneyMap',
    component: loadView('MobileJourneyMap'),
    meta: {titleKey: 'nav.pageTitleMobileJourneyMap', title: 'Mobile Journey Map', requiresAuth: true},
    beforeEnter: redirectObserverToProgressOverview
  },
  // {
  //   path: '/journey-template/map/:id',
  //   name: 'JourneyReusableTemplateMap',
  //   component: loadView('JourneyMap'),
  //   meta: {
  //     title: 'JourneyReusableTemplateMap',
  //     requiresAuth: true,
  //     allowedRoles: [roleNames.TenantAdmin, roleNames.Facilitator]
  //   }
  // },
  {
    path: '/journey-template/view/:id',
    name: 'JourneyReusableTemplateView',
    component: loadView('JourneyView'),
    meta: {
      title: 'JourneyReusableTemplateView',
      requiresAuth: true,
      allowedRoles: [roleNames.TenantAdmin, roleNames.Facilitator]
    }
  },
  {
    path: '/journey-template/create/:id',
    name: 'JourneyReusableTemplateCreation',
    component: loadView('JourneyCreation'),
    meta: {
      title: 'JourneyReusableTemplateCreation',
      requiresAuth: true,
      allowedRoles: [roleNames.TenantAdmin, roleNames.Facilitator]
    }
  },
  {
    path: '/admin',
    name: 'Admin',
    component: loadView('Admin'),
    meta: {titleKey: 'nav.pageTitleAdmin', title: 'Admin', requiresAuth: true, allowedRoles: [roleNames.TenantAdmin]},
    children: [
      {
        path: 'tenant-settings',
        name: 'AdminTenantSettings',
        component: loadComponent('admin/TenantSettings'),
        meta: {titleKey: 'nav.pageTitleTenantSettings', title: 'Tenant Settings', requiresAuth: true, allowedRoles: [roleNames.TenantAdmin]}
      },
      {
        path: 'user-management',
        name: 'AdminUserManagement',
        component: loadComponent('admin/UserManagement'),
        meta: {titleKey: 'nav.pageTitleUserManagement', title: 'User Management', requiresAuth: true, allowedRoles: [roleNames.TenantAdmin]}
      },
      {
        path: 'journey-management',
        name: 'AdminJourneyManagement',
        component: loadComponent('admin/JourneyManagement'),
        meta: {titleKey: 'nav.pageTitleJourneyManagement', title: 'Journey Management', requiresAuth: true, allowedRoles: [roleNames.TenantAdmin]}
      },
      {
        path: 'email-templates',
        name: 'AdminEmailTemplates',
        component: loadComponent('common/EmailTemplates'),
        meta: {titleKey: 'nav.pageTitleEmailTemplates', title: 'Email Templates', requiresAuth: true, allowedRoles: [roleNames.TenantAdmin]}
      },
      {
        path: 'billing',
        name: 'AdminBilling',
        component: loadComponent('admin/Billing'),
        meta: {titleKey: 'nav.pageTitleBilling', title: 'Billing', requiresAuth: true, allowedRoles: [roleNames.TenantAdmin]}
      },
      {
        path: 'journey-reusable-templates',
        name: 'AdminJourneyReusableTemplates',
        component: () => import('@/views/Admin.vue'),
        meta: {
          title: 'Journey Reusable Templates',
          requiresAuth: true,
          requiresRole: ['TenantAdmin'],
          selectedTab: 'Journey Reusable Templates'
        }
      },
    ]
  },
  {
    path: '/account',
    name: 'Account',
    component: loadView('Account'),
    meta: {titleKey: 'nav.pageTitleAccount', title: 'Account', requiresAuth: true, allowedRoles: [roleNames.TenantAdmin, roleNames.Facilitator]},
    beforeEnter: redirectToMobile('MobileAccount'),
    children: [
      {
        path: 'user-management',
        name: 'AccountUserManagement',
        component: loadComponent('account/UserManagement'),
        meta: {titleKey: 'nav.pageTitleUserManagement', title: 'User Management', requiresAuth: true, allowedRoles: [roleNames.TenantAdmin, roleNames.Facilitator]}
      },
      {
        path: 'journey-management',
        name: 'AccountJourneyManagement',
        component: loadComponent('account/JourneyManagementMyAccount'),
        meta: {titleKey: 'nav.pageTitleJourneyManagement', title: 'Journey Management', requiresAuth: true, allowedRoles: [roleNames.TenantAdmin, roleNames.Facilitator]}
      },
      {
        path: 'email-templates',
        name: 'AccountEmailTemplates',
        component: loadComponent('common/EmailTemplates'),
        meta: {titleKey: 'nav.pageTitleEmailTemplates', title: 'Email Templates', requiresAuth: true, allowedRoles: [roleNames.TenantAdmin, roleNames.Facilitator]}
      },
      {
        path: 'billing',
        name: 'AccountBilling',
        component: loadComponent('account/Billing'),
        meta: {titleKey: 'nav.pageTitleBilling', title: 'Billing', requiresAuth: true, allowedRoles: [roleNames.TenantAdmin, roleNames.Facilitator]}
      },
    ]
  },
  {
    path: '/m-account',
    name: 'MobileAccount',
    component: loadView('MobileAccount'),
    meta: {titleKey: 'nav.pageTitleMobileAccount', title: 'Mobile Account', requiresAuth: true}
  },
  {
    path: '/settings',
    name: 'Settings',
    component: loadView('Settings'),
    meta: {titleKey: 'nav.pageTitleSettings', title: 'Settings', requiresAuth: true},
    beforeEnter: redirectToMobile('MobileSettings')
  },
  {
    path: '/m-settings',
    name: 'MobileSettings',
    component: loadView('MobileSettings'),
    meta: {titleKey: 'nav.pageTitleSettings', title: 'Settings', requiresAuth: true}
  },
  {
    path: '/payment/success',
    name: 'PaymentSuccess',
    component: loadView('PaymentSuccess'),
    meta: {titleKey: 'nav.pageTitlePaymentSuccess', title: 'Payment Success', requiresAuth: false}
  },
  {
    path: '/payment/cancel',
    name: 'PaymentCancel',
    component: loadView('PaymentCanceled'),
    meta: {titleKey: 'nav.pageTitlePaymentCanceled', title: 'Payment Canceled', requiresAuth: false}
  },
  // --- Digistore24 B2C routes (public; auth handled in-component via OTP) ---
  {
    path: '/page/:slug',
    alias: '/landing/:slug',
    name: 'TrainingLanding',
    component: loadView('ds24/TrainingLanding'),
    meta: {titleKey: 'nav.pageTitleTraining', title: 'Training', requiresAuth: false, b2c: true}
  },
  {
    path: '/purchase/confirmed',
    name: 'PurchaseConfirmed',
    component: loadView('ds24/PurchaseConfirmed'),
    meta: {titleKey: 'nav.pageTitlePurchaseConfirmed', title: 'Purchase Confirmed', requiresAuth: false, b2c: true}
  },
  {
    path: '/training/:slug',
    name: 'B2CJourneyView',
    component: loadView('ds24/B2CJourneyView'),
    meta: {titleKey: 'nav.pageTitleYourTraining', title: 'Your Training', requiresAuth: false, b2c: true}
  },
  {
    path: '/chat',
    name: 'Chat',
    component: loadView('Chat'),
    meta: {titleKey: 'nav.pageTitleChat', title: 'Chat', requiresAuth: true},
    // beforeEnter: redirectToMobile('MobileChat')
    beforeEnter: blockObserverAppRole('MobileChat')
  },
  {
    path: '/m-chat',
    name: 'MobileChat',
    component: loadView('MobileChat'),
    meta: {titleKey: 'nav.pageTitleChat', title: 'Chat', requiresAuth: true},
    beforeEnter: blockObserverAppRole()
  },
  {
    path: '/tasks',
    name: 'Tasks',
    component: loadView('Tasks'),
    meta: {title: 'Tasks', requiresAuth: true},
    beforeEnter: (to, from, next) => {
      // Observer accounts don't do tasks, they only follow participants
      if (!store.getters['tenant/isTasksPageEnabled'] || store.getters['auth/isCurrentUserObserverAppRole']) {
        next({ path: '/' });
      } else {
        if (isMobileDevice()) {
          next({ name: 'MobileTasks' });
          return;
        }
        next();
      }
    },
  },
  {
    path: '/m-tasks',
    name: 'MobileTasks',
    component: loadView('MobileTasks'),
    meta: {title: 'Tasks', requiresAuth: true},
    beforeEnter: (to, from, next) => {
      // Observer accounts don't do tasks, they only follow participants
      if (!store.getters['tenant/isTasksPageEnabled'] || store.getters['auth/isCurrentUserObserverAppRole']) {
        next({ path: '/' });
      } else {
        next();
      }
    },
  },
  {
    path: '/:pathMatch(.*)*',
    name: '404',
    component: loadView('PageNotFound'),
    meta: {titleKey: 'nav.pageTitlePageNotFound', title: 'PageNotFound', requiresAuth: false}
  },
  {
    path: '/logon',
    name: 'Logon',
    component: loadComponent('auth/Logon'),
    meta: {titleKey: 'nav.pageTitleLogon', title: 'Logon', requiresAuth: false},
    beforeEnter: async (to, from, next) => {
      try {
        const user_route = await checkCurrentAuthenticatedUser();
        if (user_route) {
          next({name: isMobileDevice() ? 'MobileDashboard' : 'Dashboard'});
        } else {
          next();
        }
      } catch {
        next();
      }
    }
  },
  {
    path: '/cmv',
    name: 'Cmv',
    component: loadView('Cmv'),
    meta: {titleKey: 'nav.pageTitleCmvAdminCenter', title: 'CMV Admin Center', requiresAuth: true, allowedRoles: [roleNames.TenantAdmin]},
    beforeEnter: async (to, from, next) => {
      const allowedEmails = ['mhairer@gmail.com', 'mail@rupertseewald.com'];
      const allowedTenants = ['4fcb7cdd-9d17-4005-90b5-a047934bfce9', 'aab284e9-8cad-44e4-ac48-e2a6d86ae9d5'];

      // On a hard reload the auth/tenant store can be momentarily empty even
      // though the global beforeEach has already refreshed the token, which
      // would falsely bounce an authorized admin back to '/'. Wait briefly for
      // the store to populate before evaluating the allowlist. This allowlist is
      // only a UX gate - the backend validateCMVAccess is the real guard on
      // every CMV endpoint.
      const isReady = () => {
        const u = store.state.auth.userInfo;
        const t = store.state.tenant.tenantinfo;
        return !!(u && u.email && t && t.id);
      };
      const deadline = Date.now() + 5000;
      while (!isReady() && Date.now() < deadline) {
        await new Promise((resolve) => setTimeout(resolve, 100));
      }

      const user = store.state.auth.userInfo;
      const tenant = store.state.tenant.tenantinfo;
      if (user && allowedEmails.includes(user.email) && tenant && allowedTenants.includes(tenant.id)) {
        next();
      } else {
        next({path: '/'});
      }
    }
  },
];

// Observers don't get the journey content (the backend refuses fetch-journey for them),
// so any way into the journey pages sends them to their followed participants' progress instead.
// Resolved before the page fetches anything; the backend stays the real guard.
async function redirectObserverToProgressOverview(to, from, next) {
  const journeyID = to.params.id;

  if (!journeyID) {
    next();
    return;
  }

  let journeyRole = null;

  try {
    journeyRole = await store.dispatch('journey/getMyJourneyRole', { journeyID });
  } catch {
    // offline or failed: fall back to the cached journeys list
    const cachedJourney = store.state.journey.userJourneys.find((journey) => journey.ID === journeyID);
    journeyRole = cachedJourney ? cachedJourney.JourneyRole : null;
  }

  if (journeyRole === 'Observer') {
    next({ name: 'ParticipantsJourneyProgressOverview', params: { journeyId: journeyID } });
    return;
  }

  next();
}

// Observer accounts only follow participants: pages they can't use (Treasure Chest, Chat) send them home.
// Optionally redirects to the mobile route afterwards, via redirectToMobile.
function blockObserverAppRole(mobileRouteName = null) {
  return (to, from, next) => {
    if (store.getters['auth/isCurrentUserObserverAppRole']) {
      next({ path: '/' });
      return;
    }

    if (mobileRouteName) {
      redirectToMobile(mobileRouteName)(to, from, next);
      return;
    }

    next();
  };
}

// Helper function to redirect to mobile routes
function redirectToMobile(mobileRouteName) {
  return (to, from, next) => {
    if (isMobileDevice()) {
      next({ name: mobileRouteName, query: to.query });
    } else {
      next();
    }
  };
}

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes
});

const isUserAllowedBasedOnRole = (allowedRoles) => {
  const userRoles = store.getters['auth/getCurrentUserRoles'];
  return allowedRoles.some(role => userRoles[role]);
};

// Staff routes require Cognito login on all tenants (including B2C). Public B2C
// buyer routes (/training, etc.) use requiresAuth: false and never hit this guard.
const handleUnauthenticated = (next) => {
  next({ path: '/logon' });
};

router.beforeEach(async (to, from, next) => {
  if (to.meta.titleKey) {
    document.title = i18n.global.t(to.meta.titleKey);
  } else if (to.meta.title) {
    document.title = to.meta.title;
  }

  store.dispatch('loading/setLoading', true);

  // Check if we're coming from a CMV related route or going to dashboard
  const isComingFromCmv = from.path.includes('/cmv') || from.path.includes('/mapping');
  const isGoingToDashboard = to.path === '/dashboard' || to.path === '/';
  
  // If we're coming from CMV and going to dashboard, refresh the token
  if ((isComingFromCmv && isGoingToDashboard) || to.path === '/dashboard') {
    try {
      // Import here to avoid circular dependencies
      const { getAuthToken } = await import('@/services/auth');
      await getAuthToken(); // This will update the store with the fresh token
      console.log('Token refreshed after CMV navigation');
    } catch (error) {
      console.error('Failed to refresh token after CMV navigation:', error);
      // Continue navigation anyway, the auth system will handle any issues
    }
  }

  if (to.meta.requiresAuth) {
    try {
      // If offline and already authenticated, allow navigation
      if (!navigator.onLine && store.state.auth.loggedIn) {
        console.log('Offline: User authenticated, allowing navigation');
        // Check if the route has role restrictions
        if (to.meta.allowedRoles && !isUserAllowedBasedOnRole(to.meta.allowedRoles)) {
          // Redirect to a default page if the user doesn't have the required role
          next({ path: '/' });
        } else {
          // Allow access to the route
          next();
        }
        return;
      }

      const user_route = await checkCurrentAuthenticatedUser();
      //console.log('beforEach and with a requiredsAuth setting');
      //console.log('user_route was checkCurrentAuthenticateeUser: ', user_route);

      if (user_route) {
        //console.log('user_route defined and now getAuthToken')
        // Only refresh token if online
        if (navigator.onLine) {
          await getAuthToken(); // Ensure we have the latest token
        }

        // Check if the route has role restrictions
        if (to.meta.allowedRoles && !isUserAllowedBasedOnRole(to.meta.allowedRoles)) {
          //console.log('check is allowedRoles');
          // Redirect to a default page if the user doesn't have the required role
          next({ path: '/' });
        } else {
          //console.log('no role needed redirect to deep ling not the logon.')
          // Allow access to the route
          next();
        }
      } else {
        //console.log('user_route not defined and direct to logon');
        // Redirect to the login page if the user is not authenticated
        // (B2C tenants use the in-component OTP wall instead of Cognito).
        handleUnauthenticated(next);
      }
    } catch (error) {
      // If offline and authenticated, allow navigation
      if (!navigator.onLine && store.state.auth.loggedIn) {
        console.log('Offline: Auth error but user authenticated, allowing navigation');
        // Check if the route has role restrictions
        if (to.meta.allowedRoles && !isUserAllowedBasedOnRole(to.meta.allowedRoles)) {
          next({ path: '/' });
        } else {
          next();
        }
        return;
      }
      //console.error('Authentication error:', error);
      handleUnauthenticated(next);
    }
  } else {
    // Allow access to routes that do not require authentication
    next();
  }
});


router.afterEach((to, from) => {
  store.dispatch('loading/setLoading', false);
  
  // Check for service worker updates on navigation (but not too frequently)
  // Only check if we've navigated to a different route (not just query/hash changes)
  if (to.path !== from.path && process.env.NODE_ENV === 'production') {
    // Debounce: only check every 30 seconds max
    const lastCheck = router.lastUpdateCheck || 0;
    const now = Date.now();
    if (now - lastCheck > 30000) {
      router.lastUpdateCheck = now;
      forceServiceWorkerUpdate().catch(() => {
        // Silently fail - update checks are best effort
      });
    }
  }
});


export default router;
